import { Directive, ElementRef, Input } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: '[appDateMask]',
  standalone: false
})
export class DateMaskDirective {
  @Input() dateMask: string;

  constructor(
    private readonly el: ElementRef,
    private readonly control: NgControl
  ) {
    /**
     *
     * @param event Event
     * Fixes some issues with the mask and ads 0 to the beginning of the day/month/year
     * void
     */
    el.nativeElement.addEventListener('keyup', (event: KeyboardEvent) => {
      const keyCode = Number(event.code || event['keyCode'] || 0);
      const isDigitKeyCode =
        (keyCode >= 48 && keyCode <= 57) ||
        (keyCode >= 96 && keyCode <= 105) ||
        keyCode.toString().toLowerCase().startsWith('digit');
      const isArrowKeyCode = (keyCode >= 37 && keyCode <= 40) || keyCode.toString().toLowerCase().startsWith('arrow');
      const isBackSpaceDeleteKeyCode =
        keyCode === 8 ||
        keyCode === 46 ||
        keyCode.toString().toLowerCase() === 'backspace' ||
        keyCode.toString().toLowerCase() === 'delete';

      const dob: string = this.value;
      const splitted = dob.split('/');

      if (isDigitKeyCode) {
        if (splitted.length === 2) {
          if (splitted[0]) {
            splitted[0] = splitted[0].toString().padStart(2, '0');
          }
        }

        if (splitted.length === 3) {
          if (splitted[1]) {
            splitted[1] = splitted[1].toString().padStart(2, '0');
          }
        }

        const formattedDob = splitted.join('/');

        this.control.control.setValue(formattedDob);
        this.control.control.markAsDirty();

        // Sets the cursor at the end of the digits
        let cursorPositionAdd = 0;
        if (splitted.length === 2 || splitted.length === 1) {
          cursorPositionAdd = splitted[splitted.length - 1].length === 2 ? 1 : 0;
        }

        const cursorPosition = formattedDob.length + cursorPositionAdd;

        setTimeout(() => {
          this.el.nativeElement.setSelectionRange(cursorPosition, cursorPosition);
        }, 0);
      } else {
        if (!((isBackSpaceDeleteKeyCode || isArrowKeyCode) && dob.length > 0)) {
          setTimeout(() => {
            this.el.nativeElement.setSelectionRange(dob.length, dob.length);
          }, 0);
        }
      }

      // Sets the input dirty when date deleted
      if (this.el.nativeElement.value === this.dateMask) {
        this.control.control.setValue(null);
        this.control.control.markAsDirty();
      }
    });

    /**
     *
     * @param event Event
     * Goto month/year/next input with tab key and add 0 to day/month
     * void
     */
    el.nativeElement.addEventListener('keydown', (event: KeyboardEvent) => {
      const keyCode = event.code || event['keyCode'] || 0;
      const isTabKeyCode = keyCode === 9 || keyCode.toString().toLowerCase() === 'tab';
      const isFocused = this.el.nativeElement === document.activeElement;

      if (isTabKeyCode && isFocused) {
        const dob: string = this.value;
        const splittedDob = dob.split('/');

        // Prefill month on tab
        if (splittedDob.length === 1 && splittedDob[0].length === 2) {
          splittedDob.push('1');
        }

        // Prefill year on tab
        if (splittedDob.length === 2 && splittedDob[1].length === 2) {
          splittedDob.push('2000');
        }

        // Format date e.g. 01/01/2000
        const formattedDob = splittedDob
          .map((d, i) => (d === '' || d === '0' ? '1' : d).padStart(i === 2 ? 0 : 2, '0'))
          .join('/');

        this.control.control.setValue(formattedDob);

        if (formattedDob.length < this.dateMask.length) {
          // Sets the cursor after the next /
          this.el.nativeElement.setSelectionRange(formattedDob.length + 1, formattedDob.length + 1);

          // prevent going to next input
          event.preventDefault();
        }
      }
    });
  }

  get value(): string {
    let dob = `${this.el.nativeElement.value}`.toString().replace(/_/g, '').replace('//', '');

    if (dob.endsWith('/')) {
      dob = dob.slice(0, -1);
    }

    return dob;
  }
}
